import { useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { TeamStatsSchema } from '../teams/api'
import { TeamLogoSchema } from '../assets/api'

const getCompetitionStats = async ({
  competition_id,
  division,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  const params: Record<string, number | string> = {}
  if (division !== undefined) params.division = division
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  if (search !== undefined && search.length >= 3) params.search = search
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/stats`,
    { params }
  )
  return TeamStatsResponseSchema.parse(response.data)
}

export const useCompetitionStats = ({
  competition_id,
  division,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division?: number
  page?: number
  page_size?: number
  search?: string
}) => {
  return useSuspenseQuery({
    queryKey: [
      'teamCompetitionStats',
      { competition_id, division, page, page_size, search },
    ],
    queryFn: () =>
      getCompetitionStats({
        competition_id,
        division,
        page,
        page_size,
        search,
      }),
  })
}

const getDivisions = async ({ competition_id }: { competition_id: number }) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/divisions`
  )
  return z.array(DivisionSchema).parse(response.data)
}

export const useDivisions = ({
  competition_id,
}: {
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['divisions', { competition_id }],
    queryFn: () => getDivisions({ competition_id }),
  })
}

async function getRacesLadderStats({
  competition_id,
  division,
  team_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division?: number
  team_id?: number
  page?: number
  page_size?: number
  search?: string
}) {
  const params: Record<string, number | string> = {}
  if (division !== undefined) params.division = division
  if (team_id !== undefined) params.team_id = team_id
  if (page !== undefined) params.page = page
  if (page_size !== undefined) params.page_size = page_size
  if (search !== undefined && search.length >= 3) params.search = search
  const response = await axios.get(
    `/api/competitions/${competition_id}/race-ladder`,
    { params }
  )
  return RacesLadderStatsSchema.parse(response.data)
}

export function useRacesLadderStats({
  competition_id,
  division,
  team_id,
  page,
  page_size,
  search,
}: {
  competition_id: number
  division?: number
  team_id?: number
  page?: number
  page_size?: number
  search?: string
}) {
  return useSuspenseQuery({
    queryKey: [
      'racesLadderStats',
      { competition_id, division, team_id, page, page_size, search },
    ],
    queryFn: () =>
      getRacesLadderStats({
        competition_id,
        division,
        team_id,
        page,
        page_size,
        search,
      }),
    staleTime: 0,
  })
}

const StandingsSchema = z.object({
  avg_race_score: z.number(),
  division_id: z.number().int().positive().nullable(),
  division_name: z.string().nullable(),
  division_rank: z.number().int().positive().nullable(),
  division_rank_change: z.number().int().nullable(),
  form: z.array(z.string()),
  points: z.number().int(),
  rank: z.number().int().nonnegative(),
  rank_change: z.number().int(),
  starts: z.number(),
  team_id: z.number().int().positive(),
  team_logo: TeamLogoSchema,
  team_name: z.string(),
  top_3: z.number().int().nonnegative(),
  top_5: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
})

const RacesLadderStatsSchema = z.object({
  competition_id: z.number().int().positive(),
  round_number: z.number().int().positive(),
  items: z.array(StandingsSchema),
  total: z.number(),
})

export const DivisionSchema = z.object({
  division_id: z.number(),
  name: z.string(),
})

const TeamStatsResponseSchema = z.object({
  items: z.array(TeamStatsSchema),
  total: z.number(),
})

export type Division = z.infer<typeof DivisionSchema>
export type TeamStatsResponse = z.infer<typeof TeamStatsResponseSchema>
export type Standings = z.infer<typeof StandingsSchema>
export type RacesLadderStats = z.infer<typeof RacesLadderStatsSchema>
